import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import {
  ConnectingMethodHelper,
  MiddlePointHelper,
  SupportType,
} from '../../enums';
import { getSelectedEnclosureProposition } from '../../helpers';
import { AvailableEnclosure, Project } from '../../models';
import { MainConfigurationSharedService } from '../../services/shared/main-configuration-shared.service';

@Component({
  selector: 'app-enclosure-summary',
  standalone: true,
  imports: [NzTypographyModule],
  templateUrl: './enclosure-summary.component.html',
  styleUrl: './enclosure-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnclosureSummaryComponent {
  private readonly mainConfigurationSharedService = inject(
    MainConfigurationSharedService,
  );
  private readonly destroyRef = inject(DestroyRef);

  readonly project = input<Project | null>(null);

  private readonly selectedEnclosure = computed(() =>
    getSelectedEnclosureProposition(this.project()),
  );

  readonly selectedPropositions = computed<AvailableEnclosure[]>(() => {
    const selected = this.selectedEnclosure();
    const propositions = selected?.propositions ?? [];
    return propositions;
  });

  readonly hasSelectedEnclosure = computed(() => {
    const hasValue = this.selectedPropositions().length > 0;
    return hasValue;
  });

  readonly cableSectionLabel = computed(
    () => `${this.getSectionLabel(this.project()?.cableSection)} mm²`,
  );

  readonly connectingMethodLabel = computed(() => {
    const connectingMethod = this.project()?.connectingMethod;
    return connectingMethod
      ? ConnectingMethodHelper.enumToLabelKey(connectingMethod)
      : '-';
  });

  readonly middlePointLabel = computed(() => {
    const middlePoint = this.project()?.middlePoint;
    return middlePoint ? MiddlePointHelper.enumToLabelKey(middlePoint) : '-';
  });

  readonly showNoSupport = computed(() => {
    const project = this.project();
    const hasEnclosures = (project?.enclosurePropositions?.length ?? 0) > 0;
    const shouldShow =
      !hasEnclosures && project?.supportType === SupportType.NO_SUPPORT;
    return shouldShow;
  });

  readonly noSupportValues = computed(() => {
    const noSupport = this.project()?.noSupport;
    return {
      nbTiers: noSupport?.nbTiers ?? '-',
      nbRows: noSupport?.nbRows ?? '-',
      nbBatteriesByRow: noSupport?.nbBatteriesByRow ?? '-',
      distanceBetweenTiers: noSupport?.distanceBetweenTiers ?? '-',
    };
  });

  constructor() {
    this.mainConfigurationSharedService
      .loadConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  formatProposition(proposition: AvailableEnclosure): string {
    const quantity =
      proposition.quantity !== undefined && proposition.quantity !== null
        ? 'x' + proposition.quantity
        : '-';
    const reference =
      proposition.cabinet?.reference ?? proposition.rack?.reference ?? '-';
    return `${reference} (${quantity}) `;
  }

  private getSectionLabel(section: number | null | undefined): string {
    if (section === null || section === undefined) {
      return '-';
    }

    return `${section}`;
  }
}
